import React from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import { psychologistClientsApi } from "../api/psychologistClientsApi";
import { sessionsApi } from "../api/sessionsApi";
import { sessionRoomApi } from "../api/sessionRoomApi";
import { chatApi } from "../api/chatApi";
import { useSubscriptionLimitsGate } from "../subscription/useSubscriptionLimitsGate";
import { useToast } from "../ui/toast/ToastProvider";
import Modal from "../components/ui/Modal";
import { resolveClientsErrorMessage } from "../utils/userFacingApiError";

const FILTER_KEYS = Object.freeze({
  ALL: "all",
  ACTIVE: "active",
  NEW: "new",
  WAITING: "waiting",
  LONG: "long",
  ARCHIVE: "archive",
});

const STATUS_LABELS = Object.freeze({
  active: "Активный",
  new: "Новый",
  waiting: "Ждёт ответа",
  archive: "Архив",
});

const SESSION_STATUS_LABELS = Object.freeze({
  planned: "Запланирована",
  completed: "Завершена",
  cancelled: "Отменена",
  no_show: "Неявка",
});

const CLIENT_SOURCE_OPTIONS = [
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "email", label: "Email" },
  { value: "other", label: "Другое" },
];

const CLIENT_TOPIC_SUGGESTIONS = [
  "Тревожность",
  "Отношения",
  "Самооценка",
  "Выгорание",
  "Стресс",
  "Цели",
];

const STATUS_FILTER_OPTIONS = [
  { value: FILTER_KEYS.ALL, label: "Все статусы" },
  { value: FILTER_KEYS.ACTIVE, label: "Активные" },
  { value: FILTER_KEYS.WAITING, label: "Ждут ответа" },
  { value: FILTER_KEYS.NEW, label: "Новые" },
  { value: FILTER_KEYS.LONG, label: "Без сессий > 7 дней" },
  { value: FILTER_KEYS.ARCHIVE, label: "Архив" },
];

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function firstDefined(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    return value;
  }
  return null;
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dayDiff(fromDate, toDate) {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatTime(value) {
  const date = parseDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return "—";
  return `${formatDate(date)} · ${formatTime(date)}`;
}

const MSK_UTC_OFFSET_HOURS = 3;

function pad2(value) {
  return String(Math.max(0, Number(value) || 0)).padStart(2, "0");
}

function normalizeRuDateDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function normalizeRuTimeDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function pickText(...values) {
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (normalized) return normalized;
  }
  return "";
}

function buildGuestConferenceInviteUrl(rawInviteUrl, roomToken) {
  const token = String(roomToken || "").trim();
  if (!token) return "";
  const fallbackPath = `/conference/room/${encodeURIComponent(token)}`;
  const fallbackAbsolute = `${window.location.origin}${fallbackPath}`;
  const source = String(rawInviteUrl || "").trim();
  if (!source) return fallbackAbsolute;
  try {
    const parsed = new URL(source, window.location.origin);
    const guest = new URL(fallbackPath, window.location.origin);
    ["invite_token", "invite", "token"].forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    });
    [
      "relay_transcript",
      "relay_url",
      "relay_token",
      "relay_project",
      "relay_mode",
      "relay_delay_ms",
      "relay_min_entries",
      "relay_timeout_ms",
      "relay_roles",
    ].forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    });
    return guest.toString();
  } catch {
    return fallbackAbsolute;
  }
}

function toMoscowDateDraftFromDate(value) {
  const date = parseDate(value);
  if (!date) return "";
  const msk = new Date(date.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  return `${pad2(msk.getUTCDate())}.${pad2(msk.getUTCMonth() + 1)}.${msk.getUTCFullYear()}`;
}

function toMoscowTimeDraftFromDate(value) {
  const date = parseDate(value);
  if (!date) return "";
  const msk = new Date(date.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  return `${pad2(msk.getUTCHours())}:${pad2(msk.getUTCMinutes())}`;
}

function parseMoscowDateTimeInput(dateValue, timeValue) {
  const datePart = normalizeRuDateDraft(dateValue);
  const timePart = String(timeValue || "").trim();
  const dateMatch = datePart.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dateMatch) return null;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(timePart)) return null;

  const day = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const hour = Number(timePart.slice(0, 2));
  const minute = Number(timePart.slice(3, 5));
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return null;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;

  const utcMs = Date.UTC(year, month - 1, day, hour - MSK_UTC_OFFSET_HOURS, minute, 0, 0);
  const utcDate = new Date(utcMs);
  if (Number.isNaN(utcDate.getTime())) return null;

  const checkDate = toMoscowDateDraftFromDate(utcDate);
  const checkTime = toMoscowTimeDraftFromDate(utcDate);
  if (checkDate !== datePart || checkTime !== timePart) return null;
  return utcDate;
}

function buildDefaultStartDateTime() {
  const date = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  date.setUTCSeconds(0, 0);
  const minute = date.getUTCMinutes();
  const addMinutes = (30 - (minute % 30)) % 30 || 30;
  date.setUTCMinutes(minute + addMinutes);

  return new Date(date.getTime() - MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
}

function formatSmartDateTime(value) {
  const date = parseDate(value);
  if (!date) return "—";
  const now = new Date();
  const diff = dayDiff(now, date);
  if (diff === 0) return `Сегодня · ${formatTime(date)}`;
  if (diff === 1) return `Завтра · ${formatTime(date)}`;
  if (diff === -1) return `Вчера · ${formatTime(date)}`;
  return `${formatDate(date)} · ${formatTime(date)}`;
}

function normalizeStatus(value) {
  const token = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "_");

  if (!token) return "active";
  if (["archive", "archived", "inactive", "hidden", "blocked"].includes(token)) return "archive";
  if (["new", "pending", "fresh", "lead"].includes(token)) return "new";
  if (["waiting", "needs_reply", "awaiting_reply", "unread", "unanswered"].includes(token)) return "waiting";
  return "active";
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "КЛ";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function hashString(value) {
  const raw = String(value || "");
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function avatarTone(client) {
  if (client.status === "waiting") return "amber";
  if (client.status === "new") return "lav";
  if (client.status === "archive") return "gray";
  const seed = toNumber(client.id, hashString(client.name));
  return seed % 2 === 0 ? "mint" : "lav";
}

function statusClassName(status) {
  if (status === "new") return "is-new";
  if (status === "waiting") return "is-waiting";
  if (status === "archive") return "is-archive";
  return "is-active";
}

function normalizeSessionStatus(value) {
  const token = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (!token) return "completed";
  if (token === "planned" || token === "upcoming") return "planned";
  if (token === "pending_payment" || token === "pending") return "cancelled";
  if (token === "cancelled" || token === "canceled") return "cancelled";
  if (token === "no_show" || token === "noshow" || token === "missed") return "no_show";
  return "completed";
}

function sessionStatusClassName(status) {
  if (status === "planned") return "is-waiting";
  if (status === "cancelled" || status === "no_show") return "is-archive";
  return "is-active";
}

function formatInt(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, toNumber(value, 0)));
}

function inactivityDays(client) {
  const lastSession = parseDate(client?.lastSessionAt);
  if (!lastSession) return Number.POSITIVE_INFINITY;
  return Math.max(0, dayDiff(lastSession, new Date()));
}

function isLongInactive(client) {
  if (client?.status === "archive") return false;
  return inactivityDays(client) > 7;
}

function isUpcomingToday(value) {
  const date = parseDate(value);
  if (!date) return false;
  return sameDay(date, new Date()) && date.getTime() >= Date.now();
}

function normalizeSource(value) {
  const token = String(value || "").trim().toLowerCase();
  return CLIENT_SOURCE_OPTIONS.some((item) => item.value === token) ? token : "";
}

function sourceLabel(value) {
  return CLIENT_SOURCE_OPTIONS.find((item) => item.value === normalizeSource(value))?.label || "";
}

function normalizeClient(rawClient, index = 0) {
  const raw = asObject(rawClient);
  const idRaw = firstDefined(
    raw.id,
    raw.clientId,
    raw.profileId,
    raw.externalId
  );
  const id = idRaw ?? `local-${index}`;
  const clientUserId = firstDefined(
    raw.clientUserId,
    raw.linkedClientUserId,
    raw.userId,
    raw.client?.id,
    raw.user?.id
  );
  const name = String(
    firstDefined(
      raw.name,
      raw.fullName,
      raw.clientName,
      raw.userName,
      raw.displayName,
      raw.login
    ) || `Клиент #${index + 1}`
  ).trim();

  const status = normalizeStatus(
    firstDefined(
      raw.status,
      raw.clientStatus,
      raw.chatStatus,
      raw.lifecycleStatus,
      raw.state
    )
  );

  const sessionsCount = Math.max(
    0,
    toNumber(
      firstDefined(
        raw.sessionsCount,
        raw.completedSessionsCount,
        raw.bookingsCount,
        raw.appointmentsCount,
        raw.counts?.sessions,
        raw.counts?.appointmentsCount
      ),
      0
    )
  );

  const lastSessionAt = firstDefined(
    raw.lastSessionAt,
    raw.lastAppointmentAt,
    raw.lastVisitAt,
    raw.lastSeenAt,
    raw.updatedAt,
    raw.lastMessageAt
  );

  const nextSessionAt = firstDefined(
    raw.nextSessionAt,
    raw.nextAppointmentAt,
    raw.upcomingSessionAt,
    raw.closestBookingAt,
    raw.nextBookedAt
  );

  const note = String(
    firstDefined(
      raw.note,
      raw.lastNote,
      raw.request,
      raw.shortComment,
      raw.subtitle,
      raw.description
    ) || "Заметка пока не добавлена"
  ).trim();

  const contact = String(
    firstDefined(raw.phone, raw.email, raw.contact, raw.login, raw.telegram, "—")
  ).trim();

  const topic = String(
    firstDefined(raw.topic, raw.requestTopic, raw.tags?.[0], raw.mainRequest, raw.goal) || "—"
  ).trim();
  const source = normalizeSource(firstDefined(raw.source, raw.clientSource, raw.origin));

  const createdAt = firstDefined(raw.createdAt, raw.createdDate, raw.registrationDate, raw.invitedAt);

  return {
    id,
    clientUserId: clientUserId ?? null,
    name,
    initials: initials(name),
    status,
    sessionsCount,
    source,
    note,
    contact,
    topic,
    createdAt: parseDate(createdAt)?.toISOString() || null,
    lastSessionAt: parseDate(lastSessionAt)?.toISOString() || null,
    nextSessionAt: parseDate(nextSessionAt)?.toISOString() || null,
  };
}

function hasClientNote(client) {
  const note = String(client?.note || "").trim();
  return Boolean(note) && note !== "Заметка пока не добавлена";
}

function filterClients(clients, filterKey, query, advancedFilters = {}) {
  let result = [...clients];

  const selectedStatuses = Array.isArray(advancedFilters.statuses) ? advancedFilters.statuses : [];
  if (selectedStatuses.length) {
    result = result.filter((client) => selectedStatuses.includes(client.status));
  } else if (filterKey === FILTER_KEYS.ACTIVE) {
    result = result.filter((client) => client.status === "active");
  } else if (filterKey === FILTER_KEYS.NEW) {
    result = result.filter((client) => client.status === "new");
  } else if (filterKey === FILTER_KEYS.WAITING) {
    result = result.filter((client) => client.status === "waiting");
  } else if (filterKey === FILTER_KEYS.LONG) {
    result = result.filter((client) => isLongInactive(client));
  } else if (filterKey === FILTER_KEYS.ARCHIVE) {
    result = result.filter((client) => client.status === "archive");
  }

  if (Array.isArray(advancedFilters.topics) && advancedFilters.topics.length) {
    const topicSet = new Set(advancedFilters.topics.map((item) => String(item).trim().toLowerCase()).filter(Boolean));
    result = result.filter((client) => {
      const topic = String(client.topic || "").trim().toLowerCase();
      return Array.from(topicSet).some((item) => topic.includes(item));
    });
  }

  if (Array.isArray(advancedFilters.sources) && advancedFilters.sources.length) {
    const sourceSet = new Set(advancedFilters.sources.map(normalizeSource).filter(Boolean));
    result = result.filter((client) => sourceSet.has(normalizeSource(client.source)));
  }

  if (advancedFilters.lastSessionDate) {
    const selectedDate = parseDate(advancedFilters.lastSessionDate);
    if (selectedDate) {
      result = result.filter((client) => sameDay(parseDate(client.lastSessionAt), selectedDate));
    }
  }

  if (advancedFilters.hasUpcomingSession) {
    result = result.filter((client) => Boolean(parseDate(client.nextSessionAt)));
  }

  if (advancedFilters.longWithoutSession) {
    result = result.filter((client) => isLongInactive(client));
  }

  if (advancedFilters.hasNote) {
    result = result.filter((client) => hasClientNote(client));
  }

  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (normalizedQuery) {
    result = result.filter((client) => {
      const statusLabel = STATUS_LABELS[client.status] || "";
      const clientSourceLabel = sourceLabel(client.source);
      const haystack = [
        client.name,
        client.note,
        client.topic,
        client.contact,
        clientSourceLabel,
        statusLabel,
        formatDate(client.nextSessionAt),
        formatDate(client.lastSessionAt),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }

  return result.sort((a, b) => {
    if (a.status !== b.status) {
      if (a.status === "waiting") return -1;
      if (b.status === "waiting") return 1;
      if (a.status === "new") return -1;
      if (b.status === "new") return 1;
      if (a.status === "archive") return 1;
      if (b.status === "archive") return -1;
    }

    const aNext = parseDate(a.nextSessionAt)?.getTime() || Number.POSITIVE_INFINITY;
    const bNext = parseDate(b.nextSessionAt)?.getTime() || Number.POSITIVE_INFINITY;
    if (aNext !== bNext) return aNext - bNext;

    const aLast = parseDate(a.lastSessionAt)?.getTime() || 0;
    const bLast = parseDate(b.lastSessionAt)?.getTime() || 0;
    return bLast - aLast;
  });
}

function normalizeSession(rawSession, index = 0) {
  const raw = asObject(rawSession);
  const id = firstDefined(raw.id, raw.sessionId, raw.bookingId, raw.eventId, `session-${index + 1}`);
  const date = firstDefined(
    raw.startDateTime,
    raw.startAt,
    raw.sessionAt,
    raw.date,
    raw.createdAt,
    raw.updatedAt
  );
  const title = String(
    firstDefined(raw.title, raw.topic, raw.note, raw.comment, raw.description) || "Сессия"
  ).trim();
  const status = normalizeSessionStatus(firstDefined(raw.status, raw.sessionStatus, raw.bookingStatus));

  return {
    id,
    date: parseDate(date)?.toISOString() || null,
    title: title || "Сессия",
    status,
  };
}

export default function PsychologistClients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { me, role, logout } = useAuth();
  const { ensureWithinLimit, limitModal } = useSubscriptionLimitsGate();
  const { unreadNotificationsCount = 0 } = useNotifications();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const [clientsCounts, setClientsCounts] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [filterKey, setFilterKey] = React.useState(FILTER_KEYS.ALL);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [inviteCopied, setInviteCopied] = React.useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);
  const [showStatusMenu, setShowStatusMenu] = React.useState(false);
  const [newClientSource, setNewClientSource] = React.useState("telegram");
  const [newClientName, setNewClientName] = React.useState("");
  const [newClientContact, setNewClientContact] = React.useState("");
  const [newClientTopic, setNewClientTopic] = React.useState("");
  const [newClientNote, setNewClientNote] = React.useState("");
  const [advancedStatusFilters, setAdvancedStatusFilters] = React.useState([]);
  const [advancedSourceFilters, setAdvancedSourceFilters] = React.useState([]);
  const [advancedTopicFilters, setAdvancedTopicFilters] = React.useState([]);
  const [advancedLastSessionDate, setAdvancedLastSessionDate] = React.useState("");
  const [advancedHasUpcomingSession, setAdvancedHasUpcomingSession] = React.useState(false);
  const [advancedLongWithoutSession, setAdvancedLongWithoutSession] = React.useState(false);
  const [advancedHasNote, setAdvancedHasNote] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState(
    () => String(searchParams.get("clientId") || "").trim() || null
  );
  const [drawerTab, setDrawerTab] = React.useState("info");
  const [noteDraft, setNoteDraft] = React.useState("");
  const [savingNote, setSavingNote] = React.useState(false);
  const [addingClient, setAddingClient] = React.useState(false);
  const [chatOpeningClientId, setChatOpeningClientId] = React.useState("");
  const [busyClientActionId, setBusyClientActionId] = React.useState("");
  const [issuingAuthLinkClientId, setIssuingAuthLinkClientId] = React.useState("");
  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [sessionsError, setSessionsError] = React.useState("");
  const [sessionsByClientId, setSessionsByClientId] = React.useState({});
  const [diaryLoading, setDiaryLoading] = React.useState(false);
  const [diaryError, setDiaryError] = React.useState("");
  const [diaryByClientUserId, setDiaryByClientUserId] = React.useState({});
  const [inviteLink, setInviteLink] = React.useState("");
  const [clientAuthLinksById, setClientAuthLinksById] = React.useState({});
  const [bookingModalClient, setBookingModalClient] = React.useState(null);
  const [bookingDateInput, setBookingDateInput] = React.useState("");
  const [bookingTimeInput, setBookingTimeInput] = React.useState("");
  const [bookingDurationMinutes, setBookingDurationMinutes] = React.useState(50);
  const [bookingSaving, setBookingSaving] = React.useState(false);

  const normalizedRole = String(role || "").replace(/^ROLE_/, "").toUpperCase();
  const isPsychologist = normalizedRole === "PSYCHOLOGIST" || normalizedRole === "PSYCHO";
  const canOpenLiteratureEditor = new Set(["ADMIN", "PSYCHOLOGIST", "MODERATOR"]).has(normalizedRole);
  const userName = me?.name || me?.fullName || me?.login || me?.email || "Психолог";
  const fallbackInviteLink = React.useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://burosoul.ru";
    const id = me?.id ? String(me.id) : "";
    return id ? `${origin}/psychologist/${id}` : `${origin}/psychologist`;
  }, [me?.id]);

  const requestedClientId = React.useMemo(
    () => String(searchParams.get("clientId") || "").trim(),
    [searchParams]
  );

  const selectedClient = React.useMemo(
    () =>
      clients.find((client) => {
        const selectedKey = String(selectedClientId || "").trim();
        if (!selectedKey) return false;
        return String(client.id) === selectedKey || String(client.clientUserId || "") === selectedKey;
      }) || null,
    [clients, selectedClientId]
  );
  const selectedHistory = React.useMemo(() => {
    if (!selectedClient) return [];
    return sessionsByClientId[String(selectedClient.id)] || [];
  }, [selectedClient, sessionsByClientId]);

  const selectedDiaryEntries = React.useMemo(() => {
    if (!selectedClient?.clientUserId) return [];
    return diaryByClientUserId[String(selectedClient.clientUserId)] || [];
  }, [selectedClient, diaryByClientUserId]);
  const selectedClientAuthLink = React.useMemo(() => {
    if (!selectedClient) return null;
    return clientAuthLinksById[String(selectedClient.id)] || null;
  }, [clientAuthLinksById, selectedClient]);

  const localActiveCount = React.useMemo(
    () => clients.filter((client) => client.status === "active").length,
    [clients]
  );
  const localNewCount = React.useMemo(
    () => clients.filter((client) => client.status === "new").length,
    [clients]
  );
  const localWaitingCount = React.useMemo(
    () => clients.filter((client) => client.status === "waiting").length,
    [clients]
  );
  const localArchiveCount = React.useMemo(
    () => clients.filter((client) => client.status === "archive").length,
    [clients]
  );
  const localLongCount = React.useMemo(
    () => clients.filter((client) => isLongInactive(client)).length,
    [clients]
  );

  const activeCount = toNumber(firstDefined(clientsCounts?.active, clientsCounts?.ACTIVE), localActiveCount);
  const newCount = toNumber(firstDefined(clientsCounts?.new, clientsCounts?.NEW), localNewCount);
  const waitingCount = toNumber(firstDefined(clientsCounts?.waiting, clientsCounts?.WAITING), localWaitingCount);
  const archiveCount = toNumber(firstDefined(clientsCounts?.archive, clientsCounts?.ARCHIVE), localArchiveCount);
  const longCount = toNumber(firstDefined(clientsCounts?.long, clientsCounts?.LONG), localLongCount);
  const totalCount = toNumber(firstDefined(clientsCounts?.all, clientsCounts?.ALL), clients.length);

  const advancedFilters = React.useMemo(() => ({
    statuses: advancedStatusFilters,
    sources: advancedSourceFilters,
    topics: advancedTopicFilters,
    lastSessionDate: advancedLastSessionDate,
    hasUpcomingSession: advancedHasUpcomingSession,
    longWithoutSession: advancedLongWithoutSession,
    hasNote: advancedHasNote,
  }), [
    advancedHasNote,
    advancedHasUpcomingSession,
    advancedLastSessionDate,
    advancedLongWithoutSession,
    advancedSourceFilters,
    advancedStatusFilters,
    advancedTopicFilters,
  ]);

  const clientsVisible = React.useMemo(
    () => filterClients(clients, filterKey, searchQuery, advancedFilters),
    [clients, filterKey, searchQuery, advancedFilters]
  );

  const todayUpcoming = React.useMemo(() => {
    const upcoming = clients
      .filter((client) => isUpcomingToday(client.nextSessionAt))
      .sort((a, b) => {
        const aTs = parseDate(a.nextSessionAt)?.getTime() || 0;
        const bTs = parseDate(b.nextSessionAt)?.getTime() || 0;
        return aTs - bTs;
      });
    return upcoming.slice(0, 3);
  }, [clients]);

  const attentionClients = React.useMemo(() => {
    return clients
      .filter((client) => client.status === "waiting" || isLongInactive(client))
      .slice(0, 3);
  }, [clients]);

  const shownClientsCountLabel = React.useMemo(() => {
    if (!clientsVisible.length) return "Показано 0 клиентов";
    return `Показано 1–${formatInt(clientsVisible.length)} из ${formatInt(totalCount)} клиентов`;
  }, [clientsVisible.length, totalCount]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");

    psychologistClientsApi
      .listItems({ page: 0, size: 500 })
      .then(({ raw, items }) => {
        if (cancelled) return;
        const normalized = items.map(normalizeClient).filter(Boolean);
        const rawCounts = asObject(raw?.counts);
        setClients(normalized);
        setClientsCounts(Object.keys(rawCounts).length ? rawCounts : null);
      })
      .catch((error) => {
        if (cancelled) return;

        setClients([]);
        setClientsCounts(null);
        setLoadError(resolveClientsErrorMessage(error, "Не удалось загрузить клиентов"));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  React.useEffect(() => {
    if (!requestedClientId) return;
    setSelectedClientId(requestedClientId);
    setDrawerTab("info");
  }, [requestedClientId]);

  React.useEffect(() => {
    if (!selectedClientId) return;
    const exists = clients.some(
      (client) => String(client.id) === String(selectedClientId) || String(client.clientUserId || "") === String(selectedClientId)
    );
    if (!exists) setSelectedClientId(null);
  }, [clients, selectedClientId]);

  React.useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const selectedKey = String(selectedClient?.clientUserId || selectedClient?.id || "").trim();
    if (selectedKey) {
      if (next.get("clientId") === selectedKey) return;
      next.set("clientId", selectedKey);
      setSearchParams(next, { replace: true });
      return;
    }
    if (!next.has("clientId")) return;
    next.delete("clientId");
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedClient, setSearchParams]);

  React.useEffect(() => {
    if (!selectedClient) {
      setNoteDraft("");
      return;
    }
    setNoteDraft(selectedClient.note || "");
  }, [selectedClient]);

  React.useEffect(() => {
    let cancelled = false;
    setInviteLink(fallbackInviteLink);

    psychologistClientsApi
      .getInviteLink()
      .then((res) => {
        if (cancelled) return;
        if (res?.inviteLink) setInviteLink(res.inviteLink);
      })
      .catch(() => {
        if (cancelled) return;
        setInviteLink(fallbackInviteLink);
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackInviteLink]);

  React.useEffect(() => {
    if (!selectedClient || drawerTab !== "sessions") return;
    const cacheKey = String(selectedClient.id);
    if (sessionsByClientId[cacheKey]) {
      setSessionsError("");
      return;
    }

    let cancelled = false;
    setSessionsLoading(true);
    setSessionsError("");

    psychologistClientsApi
      .listSessions(selectedClient.id, { page: 0, size: 50 })
      .then((items) => {
        if (cancelled) return;
        setSessionsByClientId((prev) => ({
          ...prev,
          [cacheKey]: (items || []).map(normalizeSession).filter(Boolean),
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        setSessionsError(error?.message || "Не удалось загрузить историю сессий");
      })
      .finally(() => {
        if (cancelled) return;
        setSessionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [drawerTab, selectedClient, sessionsByClientId]);

  React.useEffect(() => {
    if (!selectedClient || drawerTab !== "diary") return;
    const clientUserId = String(selectedClient.clientUserId || "").trim();
    if (!clientUserId) {
      setDiaryError("Клиент ещё не привязан к аккаунту: дневник недоступен.");
      return;
    }
    if (diaryByClientUserId[clientUserId]) {
      setDiaryError("");
      return;
    }

    let cancelled = false;
    setDiaryLoading(true);
    setDiaryError("");

    psychologistClientsApi
      .listClientDiaryEntries(clientUserId, { page: 0, size: 50 })
      .then((items) => {
        if (cancelled) return;
        setDiaryByClientUserId((prev) => ({
          ...prev,
          [clientUserId]: items || [],
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        const raw = String(error?.message || error?.code || "").toUpperCase();
        if (raw.includes("DIARY_ACCESS_NOT_GRANTED")) {
          setDiaryError("Клиент пока не дал доступ к дневнику или не открыл выбранные записи.");
        } else if (raw.includes("NO_CLIENT_RELATION")) {
          setDiaryError("Дневник доступен только психологу, у которого есть история оплаченных или завершенных сессий с клиентом.");
        } else {
          setDiaryError(error?.message || "Не удалось загрузить дневник клиента");
        }
      })
      .finally(() => {
        if (cancelled) return;
        setDiaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [diaryByClientUserId, drawerTab, selectedClient]);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const handleCopyInvite = React.useCallback(async () => {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard API недоступен");
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 2200);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  }, [inviteLink, toast]);

  const handleIssueClientAuthLink = React.useCallback(
    async (client) => {
      if (!client) return;
      const clientKey = String(client.id);
      try {
        setIssuingAuthLinkClientId(clientKey);
        const result = await psychologistClientsApi.createClientAuthLink(client.id, {
          fullName: client.name,
          contact: client.contact,
        });

        if (!result?.inviteLink) {
          throw new Error("Сервер не вернул ссылку входа");
        }

        setClientAuthLinksById((prev) => ({
          ...prev,
          [clientKey]: result,
        }));

        let copied = false;
        try {
          if (!navigator?.clipboard?.writeText) throw new Error("Clipboard API недоступен");
          await navigator.clipboard.writeText(result.inviteLink);
          copied = true;
        } catch {
          copied = false;
        }

        const loginHint = result.email ? ` Логин: ${result.email}.` : "";
        toast.success(
          copied
            ? `Ссылка входа скопирована.${loginHint}`
            : `Ссылка входа создана.${loginHint}`
        );
      } catch (error) {
        toast.error(error?.message || "Не удалось создать ссылку входа");
      } finally {
        setIssuingAuthLinkClientId("");
      }
    },
    [toast]
  );

  const toggleArrayValue = React.useCallback((setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  }, []);

  const openClientDrawer = React.useCallback((client, tab = "info") => {
    if (!client) return;
    setSelectedClientId(String(client.clientUserId || client.id));
    setDrawerTab(tab);
  }, []);

  const resetAdvancedFilters = React.useCallback(() => {
    setAdvancedStatusFilters([]);
    setAdvancedSourceFilters([]);
    setAdvancedTopicFilters([]);
    setAdvancedLastSessionDate("");
    setAdvancedHasUpcomingSession(false);
    setAdvancedLongWithoutSession(false);
    setAdvancedHasNote(false);
    setFilterKey(FILTER_KEYS.ALL);
    setSearchQuery("");
  }, []);

  const closeAddClientDrawer = React.useCallback(() => {
    if (addingClient) return;
    setShowAddForm(false);
  }, [addingClient]);


  const handleOpenChat = React.useCallback(
    async (client) => {
      if (!client) return;
      const clientKey = String(client.id);
      try {
        setChatOpeningClientId(clientKey);
        const opened = await chatApi.openClientDialog(client.id);
        const did = String(opened?.chatId || "").trim();
        navigate(did ? `/chat?chatId=${encodeURIComponent(did)}` : "/chat");
      } catch (error) {
        toast.error(error?.message || "Не удалось открыть чат");
      } finally {
        setChatOpeningClientId("");
      }
    },
    [navigate, toast]
  );

  const openBookingModal = React.useCallback((client) => {
    if (!client) return;
    const defaultStart = buildDefaultStartDateTime();
    setBookingModalClient(client);
    setBookingDateInput(toMoscowDateDraftFromDate(defaultStart));
    setBookingTimeInput(toMoscowTimeDraftFromDate(defaultStart));
    setBookingDurationMinutes(50);
  }, []);

  const closeBookingModal = React.useCallback(() => {
    if (bookingSaving) return;
    setBookingModalClient(null);
  }, [bookingSaving]);

  const handleSchedule = React.useCallback(
    (client) => {
      openBookingModal(client);
    },
    [openBookingModal]
  );

  const handleCreateFreeBooking = React.useCallback(async () => {
    const client = bookingModalClient;
    if (!client) return;

    const startDate = parseMoscowDateTimeInput(bookingDateInput, bookingTimeInput);
    if (!startDate) {
      toast.error("Укажите корректную дату и время");
      return;
    }

    const duration = Math.max(20, Math.min(180, Number(bookingDurationMinutes) || 50));
    const endDate = new Date(startDate.getTime() + duration * 60 * 1000);
    if (startDate.getTime() <= Date.now()) {
      toast.error("Время записи должно быть в будущем");
      return;
    }

    const clientKey = String(client.id);
    setBookingSaving(true);
    setBusyClientActionId(clientKey);
    try {
      const created = await psychologistClientsApi.createBooking(
        client.id,
        {
          startDateTime: startDate.toISOString(),
          endDateTime: endDate.toISOString(),
          price: 0,
        },
        {
          psychologistId: me?.id,
        }
      );

      const bookingId = String(
        firstDefined(
          created?.bookingId,
          created?.booking_id,
          created?.id,
          created?.booking?.id
        ) || ""
      ).trim();
      const createdStatus = String(
        firstDefined(created?.status, created?.bookingStatus, created?.booking_status) || ""
      ).toUpperCase();

      if (bookingId && (createdStatus === "PENDING_PAYMENT" || createdStatus === "PENDING")) {
        await sessionsApi.updateBookingStatus(bookingId, "CONFIRMED").catch(() => null);
      }

      if (bookingId) {
        await sessionRoomApi.openOrGetByBooking(bookingId).catch(() => sessionRoomApi.openByBooking(bookingId).catch(() => null));
      }

      setBookingModalClient(null);
      setRefreshTick((value) => value + 1);
      toast.success("Клиент бесплатно записан. Слот заблокирован и конференция создана.");
    } catch (error) {
      const errCode = String(error?.code || error?.error || "").toUpperCase();
      if (errCode === "CLIENT_HAS_NO_LINKED_USER") {
        try {
          const externalCreated = await sessionsApi.createMyGroupSession({
            title: `Индивидуальная сессия · ${client.name}`,
            startDateTime: startDate.toISOString(),
            endDateTime: endDate.toISOString(),
            sessionFormat: "ONLINE",
            allowJoin: true,
            capacityClients: 1,
            minCapacity: 1,
            priceAtTime: 0,
          });

          const bookingId = String(
            firstDefined(
              externalCreated?.bookingId,
              externalCreated?.booking_id,
              externalCreated?.id,
              externalCreated?.booking?.id
            ) || ""
          ).trim();
          const externalCreatedStatus = String(
            firstDefined(externalCreated?.status, externalCreated?.bookingStatus, externalCreated?.booking?.status) || ""
          ).toUpperCase();

          if (bookingId && (externalCreatedStatus === "PENDING_PAYMENT" || externalCreatedStatus === "PENDING")) {
            await sessionsApi.updateBookingStatus(bookingId, "CONFIRMED").catch(() => null);
          }

          let roomToken = pickText(
            externalCreated?.roomToken,
            externalCreated?.room_token,
            externalCreated?.sessionRoom?.roomToken,
            externalCreated?.sessionRoom?.room_token,
            externalCreated?.room?.roomToken,
            externalCreated?.room?.room_token
          );

          if (!roomToken && bookingId) {
            const room = await sessionRoomApi.openOrGetByBooking(bookingId).catch(() => sessionRoomApi.openByBooking(bookingId).catch(() => null));
            roomToken = pickText(room?.roomToken, room?.room_token, room?.token);
          }

          const inviteRaw = roomToken ? await sessionRoomApi.getInviteLink(roomToken).catch(() => null) : null;
          const guestLink = buildGuestConferenceInviteUrl(inviteRaw?.inviteUrl || inviteRaw?.url || "", roomToken);

          if (guestLink) {
            try {
              await navigator.clipboard.writeText(guestLink);
              toast.success("Клиент без аккаунта: создана бесплатная бронь, ссылка скопирована.");
            } catch {
              toast.success("Клиент без аккаунта: создана бесплатная бронь.");
            }
          } else {
            toast.success("Клиент без аккаунта: создана бесплатная бронь.");
          }

          setBookingModalClient(null);
          setRefreshTick((value) => value + 1);
        } catch (externalError) {
          const errorId = String(externalError?.errorId || "").trim();
          toast.error(
            `${externalError?.message || "Не удалось записать внешнего клиента"}${errorId ? ` (errorId: ${errorId})` : ""}`
          );
        }
      } else {
        toast.error(error?.message || "Не удалось записать клиента");
      }
    } finally {
      setBookingSaving(false);
      setBusyClientActionId("");
    }
  }, [bookingDateInput, bookingDurationMinutes, bookingModalClient, bookingTimeInput, me?.id, toast]);

  const handleToggleArchive = React.useCallback(
    async (client) => {
      if (!client) return;
      const clientKey = String(client.id);
      try {
        setBusyClientActionId(clientKey);
        const nextStatus = client.status === "archive" ? "ACTIVE" : "ARCHIVE";
        await psychologistClientsApi.updateStatus(client.id, nextStatus);
        setRefreshTick((value) => value + 1);
        toast.success(client.status === "archive" ? "Клиент возвращён из архива" : "Клиент отправлен в архив");
      } catch (error) {
        toast.error(error?.message || "Не удалось изменить статус клиента");
      } finally {
        setBusyClientActionId("");
      }
    },
    [toast]
  );

  const handleSaveNote = React.useCallback(async () => {
    if (!selectedClient) return;
    const prepared = String(noteDraft || "").trim();

    try {
      setSavingNote(true);
      const updated = await psychologistClientsApi.update(selectedClient.id, {
        note: prepared || null,
      });
      const nextNote = String(firstDefined(updated?.note, prepared, "Заметка пока не добавлена") || "").trim() || "Заметка пока не добавлена";

      setClients((prev) =>
        prev.map((item) =>
          String(item.id) === String(selectedClient.id)
            ? { ...item, note: nextNote }
            : item
        )
      );
      toast.success("Заметка сохранена");
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить заметку");
    } finally {
      setSavingNote(false);
    }
  }, [noteDraft, selectedClient, toast]);

  const submitNewClient = React.useCallback(
    async ({ openCalendar = false } = {}) => {
      const name = String(newClientName || "").trim();
      const contact = String(newClientContact || "").trim();
      const topic = String(newClientTopic || "").trim();
      const note = String(newClientNote || "").trim();
      const sourceLabel = CLIENT_SOURCE_OPTIONS.find((item) => item.value === newClientSource)?.label || "";

      if (!name) {
        toast.error("Укажите имя клиента");
        return false;
      }

      if (!contact) {
        toast.error("Укажите контакт клиента");
        return false;
      }

      try {
        const usage = totalCount;
        const allowed = ensureWithinLimit({ featureKey: "clients", usage });
        if (!allowed) return false;

        setAddingClient(true);
        await psychologistClientsApi.create({
          name,
          contact: contact || null,
          source: newClientSource || null,
          topic: topic || null,
          note: note || (sourceLabel ? `Источник: ${sourceLabel}` : null),
        });

        setNewClientName("");
        setNewClientContact("");
        setNewClientTopic("");
        setNewClientNote("");
        setNewClientSource("telegram");
        setShowAddForm(false);
        setFilterKey(FILTER_KEYS.ALL);
        setSearchQuery("");
        setRefreshTick((value) => value + 1);
        toast.success("Клиент добавлен");
        if (openCalendar) {
          navigate("/dashboard/calendar");
        }
        return true;
      } catch (error) {
        toast.error(error?.message || "Не удалось добавить клиента");
        return false;
      } finally {
        setAddingClient(false);
      }
    },
    [ensureWithinLimit, navigate, newClientContact, newClientName, newClientNote, newClientSource, newClientTopic, toast, totalCount]
  );

  const handleAddClient = React.useCallback(async (event) => {
    event.preventDefault();
    await submitNewClient();
  }, [submitNewClient]);

  const handleAddClientAndOpenCalendar = React.useCallback(async () => {
    await submitNewClient({ openCalendar: true });
  }, [submitNewClient]);
  const archivedIsOnlyFilter = filterKey === FILTER_KEYS.ARCHIVE;

  if (!isPsychologist) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="psych-dashboard-v2 client-dashboard-v2 podcasts-hub psy-clients-page">
      <Helmet prioritizeSeoTags>
        <title>Клиенты психолога | Бюро Души</title>
        <meta
          name="description"
          content="Раздел клиентов психолога: поиск и фильтрация клиентов, заметки, история, быстрый переход в чат и запись в календарь."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="clients"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel="Психолог"
          canOpenLiteratureEditor={canOpenLiteratureEditor}
          notificationsBadge={unreadNotificationsCount}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <main className="main podcasts-main cd-main">
          <div className="topbar pc-topbar-minimal">
            <div className="topbar-left">
              <button
                type="button"
                className="btn-icon topbar-hamburger"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="page pc-page">
            <section className="pc-titlebar">
              <div>
                <h1>Клиенты</h1>
              </div>

              <div className="pc-titlebar__actions pc-top-actions">
                <button
                  type="button"
                  className={`btn-sm pc-invite-btn ${inviteCopied ? "is-copied" : ""}`}
                  onClick={handleCopyInvite}
                >
                  <span className="pc-btn-icon" aria-hidden="true">
                    {inviteCopied ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.4 4.52" />
                        <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07l1.41-1.41" />
                      </svg>
                    )}
                  </span>
                  <span>{inviteCopied ? "Скопировано" : "Пригласить клиента"}</span>
                </button>
                <button type="button" className="btn-sm mint pc-add-client-btn" onClick={() => setShowAddForm(true)}>
                  <span className="pc-btn-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </span>
                  <span>Добавить клиента</span>
                </button>
              </div>
            </section>

            {loadError ? <div className="pc-alert">{loadError}</div> : null}

            <section className="pc-metrics" aria-label="Сводка по клиентам">
              <button type="button" className={`pc-metric ${filterKey === FILTER_KEYS.ACTIVE ? "is-active" : ""}`} onClick={() => setFilterKey(FILTER_KEYS.ACTIVE)}>
                <span className="pc-metric__icon is-mint">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                </span>
                <strong>{formatInt(activeCount)}</strong>
                <small>Активные</small>
              </button>
              <button type="button" className={`pc-metric ${filterKey === FILTER_KEYS.WAITING ? "is-active" : ""}`} onClick={() => setFilterKey(FILTER_KEYS.WAITING)}>
                <span className="pc-metric__icon is-amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 1 1 17 0z" /></svg>
                </span>
                <strong>{formatInt(waitingCount)}</strong>
                <small>Ждут ответа</small>
              </button>
              <button type="button" className={`pc-metric ${filterKey === FILTER_KEYS.NEW ? "is-active" : ""}`} onClick={() => setFilterKey(FILTER_KEYS.NEW)}>
                <span className="pc-metric__icon is-lav">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15 8.5 22 9.3 17 14 18.3 21 12 17.4 5.7 21 7 14 2 9.3 9 8.5 12 2" /></svg>
                </span>
                <strong>{formatInt(newCount)}</strong>
                <small>Новые</small>
              </button>
              <button type="button" className={`pc-metric ${filterKey === FILTER_KEYS.LONG ? "is-active" : ""}`} onClick={() => setFilterKey(FILTER_KEYS.LONG)}>
                <span className="pc-metric__icon is-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                </span>
                <strong>{formatInt(longCount)}</strong>
                <small>Без сессий &gt; 7 дней</small>
              </button>
              <button type="button" className={`pc-metric ${filterKey === FILTER_KEYS.ARCHIVE ? "is-active" : ""}`} onClick={() => setFilterKey(FILTER_KEYS.ARCHIVE)}>
                <span className="pc-metric__icon is-gray">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                </span>
                <strong>{formatInt(archiveCount)}</strong>
                <small>Архив</small>
              </button>
            </section>

            <section className="pc-workspace">
              <div className="pc-content-col">
                <section className="pc-toolbar pc-toolbar--inline">
                  <label className="pc-search" htmlFor="clientsSearch">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                      id="clientsSearch"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Поиск по имени, заметке или контакту..."
                    />
                  </label>

                  <div className="pc-filter-zone">
                    <div className="pc-status-dropdown">
                      <button
                        type="button"
                        className={`pc-status-select ${showStatusMenu ? "is-open" : ""}`}
                        onClick={() => {
                          setShowStatusMenu((value) => !value);
                          setShowAdvancedFilters(false);
                        }}
                      >
                        <span>{STATUS_FILTER_OPTIONS.find((item) => item.value === filterKey)?.label || "Все статусы"}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                      </button>
                      {showStatusMenu ? (
                        <div className="pc-status-menu">
                          {STATUS_FILTER_OPTIONS.map((item) => (
                            <button
                              key={item.value}
                              type="button"
                              className={filterKey === item.value ? "is-active" : ""}
                              onClick={() => {
                                setFilterKey(item.value);
                                setShowStatusMenu(false);
                              }}
                            >
                              <span>{filterKey === item.value ? "✓" : ""}</span>
                              {item.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={`pc-filter-toggle ${showAdvancedFilters ? "is-active" : ""}`}
                      onClick={() => { setShowAdvancedFilters((value) => !value); setShowStatusMenu(false); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>
                      Фильтры
                    </button>
                    {showAdvancedFilters ? (
                      <div className="pc-filter-popover">
                        <div className="pc-filter-popover__head">
                          <strong>Фильтры</strong>
                          <button type="button" onClick={resetAdvancedFilters}>Сбросить</button>
                        </div>

                        <div className="pc-filter-group">
                          <span>Источник</span>
                          <div className="pc-filter-check-grid">
                            {CLIENT_SOURCE_OPTIONS.map((item) => {
                              const checked = advancedSourceFilters.includes(item.value);
                              return (
                                <label key={item.value} className={`pc-check ${checked ? "is-checked" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleArrayValue(setAdvancedSourceFilters, item.value)}
                                  />
                                  <span className="pc-check__box" />
                                  <span className="pc-check__label">{item.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="pc-filter-group">
                          <span>Статус</span>
                          <div className="pc-filter-check-grid">
                            {[
                              { value: "active", label: "Активный" },
                              { value: "waiting", label: "Ждёт ответа" },
                              { value: "new", label: "Новый" },
                              { value: "archive", label: "Архив" },
                            ].map((item) => {
                              const checked = advancedStatusFilters.includes(item.value);
                              return (
                                <label key={item.value} className={`pc-check ${checked ? "is-checked" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleArrayValue(setAdvancedStatusFilters, item.value)}
                                  />
                                  <span className="pc-check__box" />
                                  <span className="pc-check__label">{item.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="pc-filter-group">
                          <span>Темы</span>
                          <div className="pc-filter-chip-list">
                            {CLIENT_TOPIC_SUGGESTIONS.map((topic) => (
                              <button
                                key={topic}
                                type="button"
                                className={advancedTopicFilters.includes(topic) ? "is-active" : ""}
                                onClick={() => toggleArrayValue(setAdvancedTopicFilters, topic)}
                              >
                                {topic}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="pc-filter-group">
                          <span>Дата последней сессии</span>
                          <label className="pc-filter-date">
                            <input
                              type="date"
                              value={advancedLastSessionDate}
                              onChange={(event) => setAdvancedLastSessionDate(event.target.value)}
                            />
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                          </label>
                        </div>

                        <div className="pc-filter-group pc-filter-switches">
                          <label className="pc-switch-row">
                            <input type="checkbox" checked={advancedHasUpcomingSession} onChange={(event) => setAdvancedHasUpcomingSession(event.target.checked)} />
                            <span className="pc-switch" />
                            <span>Есть ближайшая сессия</span>
                          </label>
                          <label className="pc-switch-row">
                            <input type="checkbox" checked={advancedLongWithoutSession} onChange={(event) => setAdvancedLongWithoutSession(event.target.checked)} />
                            <span className="pc-switch" />
                            <span>Без сессий &gt; 7 дней</span>
                          </label>
                          <label className="pc-switch-row">
                            <input type="checkbox" checked={advancedHasNote} onChange={(event) => setAdvancedHasNote(event.target.checked)} />
                            <span className="pc-switch" />
                            <span>Есть заметка</span>
                          </label>
                        </div>

                        <button type="button" className="pc-filter-reset-link" onClick={resetAdvancedFilters}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
                          Сбросить фильтры
                        </button>
                      </div>
                    ) : null}
                  </div>
                </section>

                <section className="pc-table-wrap">
                  <div className="pc-table-head">
                    <div>Клиент</div>
                    <div>Статус</div>
                    <div>Последняя сессия</div>
                    <div>Контакты</div>
                    <div>Заметка</div>
                    <div></div>
                  </div>

                  {loading ? (
                    <div className="pc-skeleton-list">
                      {Array.from({ length: 7 }).map((_, index) => (
                        <div key={index} className="pc-skeleton-row" />
                      ))}
                    </div>
                  ) : clientsVisible.length ? (
                    <>
                      <div className="pc-table-body">
                        {clientsVisible.map((client) => (
                          <div
                            key={client.id}
                            className="pc-row"
                            role="button"
                            tabIndex={0}
                            onClick={() => openClientDrawer(client)}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              openClientDrawer(client);
                            }}
                          >
                            <div className="pc-cell-name">
                              <span className={`pc-avatar tone-${avatarTone(client)}`}>
                                {client.initials}
                                <span className={`pc-status-dot ${statusClassName(client.status)}`} />
                              </span>
                              <span className="pc-cell-name__meta">
                                <strong>{client.name}</strong>
                                <small>{client.topic && client.topic !== "—" ? client.topic : "Тема не указана"}</small>
                              </span>
                            </div>

                            <div>
                              <span className={`pc-status-badge ${statusClassName(client.status)}`}>
                                {STATUS_LABELS[client.status] || STATUS_LABELS.active}
                              </span>
                            </div>

                            <div className="pc-cell-muted">
                              <strong>{formatDate(client.lastSessionAt)}</strong>
                              <small>{client.lastSessionAt ? `${inactivityDays(client)} дней назад` : "Сессий пока нет"}</small>
                            </div>
                            <div className="pc-cell-contact">
                              <span>{client.contact || "—"}</span>
                            </div>
                            <div className="pc-cell-note">{client.note}</div>

                            <div className="pc-actions">
                              <button
                                type="button"
                                className="pc-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenChat(client);
                                }}
                                disabled={chatOpeningClientId === String(client.id)}
                                aria-label={`Написать ${client.name}`}
                                title="Написать"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="pc-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSchedule(client);
                                }}
                                disabled={busyClientActionId === String(client.id)}
                                aria-label={`Записать ${client.name}`}
                                title="Записать"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="4" width="18" height="18" rx="2" />
                                  <line x1="16" y1="2" x2="16" y2="6" />
                                  <line x1="8" y1="2" x2="8" y2="6" />
                                  <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="pc-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleIssueClientAuthLink(client);
                                }}
                                disabled={issuingAuthLinkClientId === String(client.id)}
                                aria-label={`Дать ссылку входа ${client.name}`}
                                title="Дать ссылку входа"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M15 7h6v6" />
                                  <path d="M10 14L21 3" />
                                  <path d="M21 14v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="pc-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleArchive(client);
                                }}
                                disabled={busyClientActionId === String(client.id)}
                                aria-label={client.status === "archive" ? "Вернуть из архива" : "Отправить в архив"}
                                title={client.status === "archive" ? "Вернуть из архива" : "В архив"}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="21 8 21 21 3 21 3 8" />
                                  <rect x="1" y="3" width="22" height="5" />
                                  <line x1="10" y1="12" x2="14" y2="12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="pc-mobile-list">
                        {clientsVisible.map((client) => (
                          <article key={`mob-${client.id}`} className="pc-mobile-card">
                            <button
                              type="button"
                              className="pc-mobile-card__main"
                              onClick={() => openClientDrawer(client)}
                            >
                              <span className={`pc-avatar tone-${avatarTone(client)}`}>{client.initials}</span>
                              <span className="pc-mobile-card__meta">
                                <strong>{client.name}</strong>
                                <small>{formatSmartDateTime(client.lastSessionAt)}</small>
                                <em>{client.note}</em>
                              </span>
                              <span className={`pc-status-badge ${statusClassName(client.status)}`}>
                                {STATUS_LABELS[client.status] || STATUS_LABELS.active}
                              </span>
                            </button>
                            <div className="pc-mobile-card__actions">
                              <button
                                type="button"
                                className="btn-sm"
                                onClick={() => handleOpenChat(client)}
                                disabled={chatOpeningClientId === String(client.id)}
                              >
                                Написать
                              </button>
                              <button
                                type="button"
                                className="btn-sm mint"
                                onClick={() => handleSchedule(client)}
                                disabled={busyClientActionId === String(client.id)}
                              >
                                Записать
                              </button>
                              <button
                                type="button"
                                className="btn-sm"
                                onClick={() => handleIssueClientAuthLink(client)}
                                disabled={issuingAuthLinkClientId === String(client.id)}
                              >
                                {issuingAuthLinkClientId === String(client.id) ? "Готовим ссылку..." : "Ссылка входа"}
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="pc-empty">
                      <div className="pc-empty__title">
                        {archivedIsOnlyFilter ? "В архиве пока нет клиентов" : "Клиенты не найдены"}
                      </div>
                      <div className="pc-empty__text">
                        Попробуйте изменить фильтры или добавьте клиента вручную.
                      </div>
                      <button type="button" className="btn-sm mint" onClick={() => setShowAddForm(true)}>
                        Добавить клиента
                      </button>
                    </div>
                  )}

                  {!loading && clientsVisible.length ? (
                    <div className="pc-table-footer">
                      <span>{shownClientsCountLabel}</span>
                      <span className="pc-table-pages"><button type="button">‹</button><strong>1</strong><button type="button">2</button><button type="button">3</button><button type="button">›</button></span>
                      <span className="pc-per-page">8 на странице</span>
                    </div>
                  ) : null}
                </section>
              </div>

              <aside className="pc-side-col">
                <section className="pc-side-card">
                  <div className="pc-side-card__head">
                    <h3>Сегодня</h3>
                    <button type="button" onClick={() => navigate("/dashboard/calendar")} aria-label="Открыть календарь">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    </button>
                  </div>
                  {todayUpcoming.length ? (
                    <div className="pc-side-list">
                      {todayUpcoming.map((client) => (
                        <button key={client.id} type="button" className="pc-today-item" onClick={() => handleSchedule(client)}>
                          <time>{formatTime(client.nextSessionAt)}</time>
                          <span className={`pc-avatar pc-avatar--sm tone-${avatarTone(client)}`}>{client.initials}</span>
                          <span><strong>{client.name}</strong><small>Онлайн-сессия</small></span>
                          <em>Запланировано</em>
                        </button>
                      ))}
                    </div>
                  ) : <div className="pc-side-empty">На сегодня записей нет</div>}
                  <button type="button" className="pc-side-link" onClick={() => navigate("/dashboard/calendar")}>Смотреть календарь →</button>
                </section>

                <section className="pc-side-card">
                  <div className="pc-side-card__head">
                    <h3>Нужно внимание <span>{attentionClients.length}</span></h3>
                  </div>
                  {attentionClients.length ? (
                    <div className="pc-attention-list">
                      {attentionClients.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          className="pc-attention-item"
                          onClick={() => {
                            setFilterKey(client.status === "waiting" ? FILTER_KEYS.WAITING : FILTER_KEYS.LONG);
                            setSearchQuery(client.name);
                            openClientDrawer(client);
                          }}
                        >
                          <span className={`pc-avatar pc-avatar--sm tone-${avatarTone(client)}`}>{client.initials}</span>
                          <span><strong>{client.name}</strong><small>{client.status === "waiting" ? "Ждёт ответа" : "Нет сессий более 7 дней"}</small></span>
                          <em>{inactivityDays(client) === Number.POSITIVE_INFINITY ? "—" : `${inactivityDays(client)} дн.`}</em>
                        </button>
                      ))}
                    </div>
                  ) : <div className="pc-side-empty">Клиентов без реакции нет</div>}
                  <button type="button" className="pc-side-link" onClick={() => { setFilterKey(FILTER_KEYS.WAITING); setSearchQuery(""); }}>Показать в таблице →</button>
                </section>

                <section className="pc-side-card pc-side-card--guide">
                  <h3>Как это работает</h3>
                  <ol>
                    <li><strong>Добавьте клиента</strong><span>Создайте профиль вручную.</span></li>
                    <li><strong>Назначьте сессию в календаре</strong><span>Выберите время и формат встречи.</span></li>
                    <li><strong>Синхронизируйте календари</strong><span>Google и Яндекс для удобства.</span></li>
                  </ol>
                  <button type="button" className="pc-side-link" onClick={() => navigate("/dashboard/settings")}>Подробнее о процессе →</button>
                </section>
              </aside>
            </section>
          </div>
        </main>
      </div>

      <button
        type="button"
        className={`pc-backdrop ${selectedClient ? "is-open" : ""}`}
        onClick={() => setSelectedClientId(null)}
        aria-label="Закрыть карточку клиента"
      />

      <aside className={`pc-drawer ${selectedClient ? "is-open" : ""}`} aria-hidden={!selectedClient}>
        {selectedClient ? (
          <>
            <div className="pc-drawer__head">
              <div className="pc-drawer__head-top">
                <div className="pc-drawer__overline">Карточка клиента</div>
                <button type="button" className="pc-drawer__close" onClick={() => setSelectedClientId(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="pc-drawer__identity">
                <span className={`pc-avatar tone-${avatarTone(selectedClient)}`}>{selectedClient.initials}</span>
                <div>
                  <div className="pc-drawer__name">{selectedClient.name}</div>
                  <div className="pc-drawer__subline">
                    ID {String(selectedClient.id).replace(/^local-/, "")} · {STATUS_LABELS[selectedClient.status] || STATUS_LABELS.active}
                  </div>
                </div>
              </div>
            </div>

            <div className="pc-drawer__tabs">
              <button type="button" className={drawerTab === "info" ? "is-active" : ""} onClick={() => setDrawerTab("info")}>
                Инфо
              </button>
              <button type="button" className={drawerTab === "sessions" ? "is-active" : ""} onClick={() => setDrawerTab("sessions")}>
                Сессии
              </button>
              <button type="button" className={drawerTab === "notes" ? "is-active" : ""} onClick={() => setDrawerTab("notes")}>
                Заметки
              </button>
              <button type="button" className={drawerTab === "diary" ? "is-active" : ""} onClick={() => setDrawerTab("diary")}>
                Дневник
              </button>
            </div>

            <div className="pc-drawer__body">
              {drawerTab === "info" ? (
                <div className="pc-drawer__section-list">
                  <section className="pc-drawer__section">
                    <h4>Контакты</h4>
                    <div className="pc-kv">
                      <span>Телефон / Email</span>
                      <strong>{selectedClient.contact || "—"}</strong>
                    </div>
                    <div className="pc-kv">
                      <span>Тема запроса</span>
                      <strong>{selectedClient.topic || "—"}</strong>
                    </div>
                    <div className="pc-kv">
                      <span>Добавлен</span>
                      <strong>{formatDate(selectedClient.createdAt)}</strong>
                    </div>
                  </section>

                  <section className="pc-drawer__section">
                    <h4>Доступ в кабинет</h4>
                    <p>
                      Можно выдать клиенту персональную ссылку входа. Если у клиента нет своей почты, сервер создаст
                      технический логин на базе ФИО и предложит сменить почту и пароль после входа.
                    </p>
                    <button
                      type="button"
                      className="btn-sm"
                      onClick={() => handleIssueClientAuthLink(selectedClient)}
                      disabled={issuingAuthLinkClientId === String(selectedClient.id)}
                    >
                      {issuingAuthLinkClientId === String(selectedClient.id) ? "Готовим ссылку..." : "Дать ссылку входа"}
                    </button>
                    {selectedClientAuthLink?.inviteLink ? (
                      <>
                        <div className="pc-kv">
                          <span>Ссылка</span>
                          <strong style={{ wordBreak: "break-all" }}>{selectedClientAuthLink.inviteLink}</strong>
                        </div>
                        {selectedClientAuthLink.email ? (
                          <div className="pc-kv">
                            <span>Логин</span>
                            <strong>{selectedClientAuthLink.email}</strong>
                          </div>
                        ) : null}
                        {selectedClientAuthLink.expiresAt ? (
                          <div className="pc-kv">
                            <span>Действует до</span>
                            <strong>{formatDateTime(selectedClientAuthLink.expiresAt)}</strong>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </section>

                  <section className="pc-drawer__section">
                    <h4>Статистика</h4>
                    <div className="pc-drawer__stats">
                      <article>
                        <strong>{formatInt(selectedClient.sessionsCount)}</strong>
                        <span>сессий всего</span>
                      </article>
                      <article>
                        <strong>{formatDateTime(selectedClient.lastSessionAt)}</strong>
                        <span>последняя сессия</span>
                      </article>
                    </div>
                  </section>

                  <section className="pc-drawer__section">
                    <h4>Запрос</h4>
                    <p>{selectedClient.note}</p>
                  </section>
                </div>
              ) : null}

              {drawerTab === "sessions" ? (
                <div className="pc-drawer__section-list">
                  <section className="pc-drawer__section">
                    <h4>История сессий</h4>
                    {sessionsLoading ? (
                      <div className="pc-history__empty">Загружаем историю…</div>
                    ) : sessionsError ? (
                      <div className="pc-history__empty">{sessionsError}</div>
                    ) : selectedHistory.length ? (
                      <div className="pc-history">
                        {selectedHistory.map((session, index) => (
                          <div key={session.id} className="pc-history__row">
                            <span className="pc-history__index">{index + 1}</span>
                            <div className="pc-history__meta">
                              <strong>{formatDateTime(session.date)}</strong>
                              <small>{session.title}</small>
                            </div>
                            <span className={`pc-status-badge ${sessionStatusClassName(session.status)}`}>
                              {SESSION_STATUS_LABELS[session.status] || SESSION_STATUS_LABELS.completed}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="pc-history__empty">Сессий пока нет</div>
                    )}
                  </section>
                </div>
              ) : null}

              {drawerTab === "diary" ? (
                <div className="pc-drawer__section-list">
                  <section className="pc-drawer__section">
                    <h4>Дневник клиента</h4>
                    {!selectedClient.clientUserId ? (
                      <div className="pc-history__empty">Клиент ещё не привязан к аккаунту, поэтому дневник недоступен.</div>
                    ) : diaryLoading ? (
                      <div className="pc-history__empty">Загружаем дневник…</div>
                    ) : diaryError ? (
                      <div className="pc-history__empty">{diaryError}</div>
                    ) : selectedDiaryEntries.length ? (
                      <div className="pc-history">
                        {selectedDiaryEntries.map((entry) => (
                          <article key={entry.id} className="pc-history__row">
                            <span className="pc-history__index">{entry.moodScore ? `${entry.moodScore}/10` : "—"}</span>
                            <div className="pc-history__meta">
                              <strong>{entry.title || "Запись дневника"}</strong>
                              <small>{formatDate(entry.entryDate || entry.createdAt)} · {entry.moodLabel || "без оценки"}</small>
                              <small>{String(entry.content || entry.text || "").slice(0, 180)}</small>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="pc-history__empty">Клиент пока не открыл записи дневника.</div>
                    )}
                  </section>
                </div>
              ) : null}

              {drawerTab === "notes" ? (
                <div className="pc-drawer__section-list">
                  <section className="pc-drawer__section">
                    <h4>Заметки</h4>
                    <textarea
                      value={noteDraft}
                      onChange={(event) => setNoteDraft(event.target.value)}
                      placeholder="Добавьте заметку о клиенте…"
                    />
                    <button type="button" className="btn-sm" onClick={handleSaveNote} disabled={savingNote}>
                      {savingNote ? "Сохраняем..." : "Сохранить заметку"}
                    </button>
                  </section>
                </div>
              ) : null}
            </div>

            <div className="pc-drawer__footer">
              <button
                type="button"
                className="btn-sm mint"
                onClick={() => handleSchedule(selectedClient)}
                disabled={busyClientActionId === String(selectedClient.id)}
              >
                Записать
              </button>
              <button
                type="button"
                className="btn-sm"
                onClick={() => handleOpenChat(selectedClient)}
                disabled={chatOpeningClientId === String(selectedClient.id)}
              >
                Написать
              </button>
              <button
                type="button"
                className="btn-sm"
                onClick={() => handleIssueClientAuthLink(selectedClient)}
                disabled={issuingAuthLinkClientId === String(selectedClient.id)}
              >
                {issuingAuthLinkClientId === String(selectedClient.id) ? "Готовим ссылку..." : "Ссылка входа"}
              </button>
            </div>
          </>
        ) : null}
      </aside>

      <button
        type="button"
        className={`pc-add-drawer-backdrop ${showAddForm ? "is-open" : ""}`}
        onClick={closeAddClientDrawer}
        aria-label="Закрыть панель добавления клиента"
      />

      <aside className={`pc-add-drawer ${showAddForm ? "is-open" : ""}`} aria-hidden={!showAddForm}>
        <div className="pc-add-drawer__head">
          <div>
            <h2>Новый клиент</h2>
            <p>Быстрое добавление</p>
          </div>
          <button type="button" className="pc-add-drawer__close" onClick={closeAddClientDrawer} disabled={addingClient} aria-label="Закрыть">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form id="pcAddClientForm" className="pc-add-drawer__form" onSubmit={handleAddClient}>
          <label className="pc-add-drawer__field">
            <span>Имя клиента <em>*</em></span>
            <input
              value={newClientName}
              onChange={(event) => setNewClientName(event.target.value)}
              placeholder="Введите имя и фамилию"
              maxLength={190}
              required
            />
          </label>

          <div className="pc-add-drawer__field">
            <span>Откуда клиент</span>
            <div className="pc-source-grid">
              {CLIENT_SOURCE_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`pc-source-chip ${newClientSource === item.value ? "is-active" : ""}`}
                  onClick={() => setNewClientSource(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <label className="pc-add-drawer__field">
            <span>Контакт <em>*</em></span>
            <input
              value={newClientContact}
              onChange={(event) => setNewClientContact(event.target.value)}
              placeholder="@username или email"
              maxLength={255}
            />
            <small>Укажите username, email или номер телефона</small>
          </label>

          <label className="pc-add-drawer__field">
            <span>Заметка</span>
            <textarea
              value={newClientNote}
              onChange={(event) => setNewClientNote(event.target.value)}
              placeholder="Короткая заметка (необязательно)"
              maxLength={200}
            />
            <small className="pc-add-drawer__counter">{newClientNote.length}/200</small>
          </label>

          <div className="pc-add-drawer__field">
            <span>Темы <small>(необязательно)</small></span>
            <div className="pc-topic-grid">
              {CLIENT_TOPIC_SUGGESTIONS.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  className={`pc-topic-chip ${newClientTopic === topic ? "is-active" : ""}`}
                  onClick={() => setNewClientTopic((prev) => (prev === topic ? "" : topic))}
                >
                  {topic}
                </button>
              ))}
              <button type="button" className="pc-topic-chip pc-topic-chip--ghost" onClick={() => setNewClientTopic("")}>
                + Добавить тему
              </button>
            </div>
          </div>

          <div className="pc-add-drawer__info">
            Вы сохраните клиента. Сессию можно назначить позже и синхронизировать с Google Calendar и Яндекс Календарём.
          </div>

          <div className="pc-add-drawer__footer">
            <button type="submit" className="btn-sm mint" disabled={addingClient}>
              {addingClient ? "Сохраняем..." : "Сохранить клиента"}
            </button>
            <button type="button" className="btn-sm" onClick={handleAddClientAndOpenCalendar} disabled={addingClient}>
              Сохранить и открыть календарь
            </button>
          </div>
        </form>
      </aside>

      <Modal
        open={Boolean(bookingModalClient)}
        title="Записать клиента"
        onClose={closeBookingModal}
        size="sm"
        className="pc-booking-modal-shell"
        cardClassName="pc-booking-modal"
        actions={(
          <>
            <button type="button" className="btn-sm" onClick={closeBookingModal} disabled={bookingSaving}>
              Отмена
            </button>
            <button type="button" className="btn-sm mint" onClick={handleCreateFreeBooking} disabled={bookingSaving}>
              {bookingSaving ? "Сохраняем..." : "Записать бесплатно"}
            </button>
          </>
        )}
      >
        <div className="pc-booking-modal__content">
          <p className="pc-booking-modal__lead">
            Клиент: <strong>{bookingModalClient?.name || "—"}</strong>
          </p>

          <div className="pc-booking-modal__grid">
            <label className="pc-booking-modal__field">
              <span>Дата</span>
              <input
                type="text"
                value={bookingDateInput}
                inputMode="numeric"
                maxLength={10}
                placeholder="ДД.ММ.ГГГГ"
                onChange={(event) => setBookingDateInput(normalizeRuDateDraft(event.target.value))}
                disabled={bookingSaving}
              />
            </label>

            <label className="pc-booking-modal__field">
              <span>Время</span>
              <input
                type="text"
                value={bookingTimeInput}
                inputMode="numeric"
                maxLength={5}
                placeholder="ЧЧ:ММ"
                onChange={(event) => setBookingTimeInput(normalizeRuTimeDraft(event.target.value))}
                disabled={bookingSaving}
              />
            </label>

            <label className="pc-booking-modal__field pc-booking-modal__field--full">
              <span>Длительность</span>
              <select
                value={String(bookingDurationMinutes)}
                onChange={(event) => setBookingDurationMinutes(Number(event.target.value) || 50)}
                disabled={bookingSaving}
              >
                <option value="30">30 минут</option>
                <option value="50">50 минут</option>
                <option value="60">60 минут</option>
                <option value="90">90 минут</option>
              </select>
            </label>
          </div>

          <p className="pc-booking-modal__note">
            Время указывается по Москве (МСК, UTC+3). Запись будет создана без оплаты: слот станет занятым в календаре, а конференция появится как обычная запись клиента.
          </p>
        </div>
      </Modal>
      {limitModal}
    </div>
  );
}
